import React, { useState, useEffect } from 'react';
import { 
  Table, Button, Space, Input, Modal, Form, message, 
  Tag, Popconfirm, Card, Typography, Switch, Badge, Tooltip,
  Select, Row, Col, DatePicker
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined,
  UserOutlined, CrownOutlined, CheckCircleOutlined, CloseCircleOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import request from '../../utils/request';
import dayjs from 'dayjs';

const { Title } = Typography;
const { Search } = Input;
const { Option } = Select;

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });
  const [searchText, setSearchText] = useState('');
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [createForm] = Form.useForm();
  const [editForm] = Form.useForm();
  const navigate = useNavigate();

  useEffect(() => {
    fetchUsers();
  }, [pagination.current, pagination.pageSize, searchText]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return null;
    }
    return { 'Authorization': `Bearer ${token}` };
  };

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      if (!headers) return;

      const response = await request.get('/admin/users', {
        params: {
          page: pagination.current,
          size: pagination.pageSize,
          search: searchText || undefined
        },
        headers
      });

      if (response.success) {
        setUsers(response.data.users);
        setPagination(prev => ({
          ...prev,
          total: response.data.pagination.total
        }));
      }
    } catch (error) {
      console.error('获取用户列表失败:', error);
      if (error.response?.status === 401) {
        message.error('管理员会话已过期，请重新登录');
        navigate('/admin/login');
      } else {
        message.error('获取用户列表失败');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (values) => {
    try {
      const headers = getAuthHeaders();
      if (!headers) return;

      const payload = {
        ...values,
        expires_at: values.expires_at ? values.expires_at.toISOString() : null
      };
      const response = await request.post('/admin/users', payload, { headers });

      if (response.success) {
        message.success('用户创建成功');
        setCreateModalVisible(false);
        createForm.resetFields();
        fetchUsers();
      } else {
        message.error(response.message || '创建用户失败');
      }
    } catch (error) {
      console.error('创建用户失败:', error);
      message.error(error.response?.data?.detail || '创建用户失败');
    }
  };

  const handleEditUser = async (values) => {
    try {
      const headers = getAuthHeaders();
      if (!headers) return;

      const payload = {
        ...values,
        expires_at: values.expires_at ? values.expires_at.toISOString() : null
      };
      const response = await request.put(`/admin/users/${editingUser.id}`, payload, { headers });

      if (response.success) {
        message.success('用户更新成功');
        setEditModalVisible(false);
        setEditingUser(null);
        editForm.resetFields();
        fetchUsers();
      } else {
        message.error(response.message || '更新用户失败');
      }
    } catch (error) {
      console.error('更新用户失败:', error);
      message.error(error.response?.data?.detail || '更新用户失败');
    }
  };

  const handleDeleteUser = async (userId) => {
    try {
      const headers = getAuthHeaders();
      if (!headers) return;

      const response = await request.delete(`/admin/users/${userId}`, { headers });

      if (response.success) {
        message.success('用户删除成功');
        fetchUsers();
      } else {
        message.error(response.message || '删除用户失败');
      }
    } catch (error) {
      console.error('删除用户失败:', error);
      message.error(error.response?.data?.detail || '删除用户失败');
    }
  };

  const handleToggleActivation = async (userId) => {
    try {
      const headers = getAuthHeaders();
      if (!headers) return;

      const response = await request.post(`/admin/users/${userId}/toggle-activation`, {}, { headers });

      if (response.success) {
        message.success(response.message);
        fetchUsers();
      } else {
        message.error(response.message || '切换用户状态失败');
      }
    } catch (error) {
      console.error('切换用户状态失败:', error);
      message.error(error.response?.data?.detail || '切换用户状态失败');
    }
  };

  const openEditModal = (user) => {
    setEditingUser(user);
    editForm.setFieldsValue({
      email: user.email,
      full_name: user.full_name,
      is_active: user.is_active,
      is_superuser: user.is_superuser,
      expires_at: user.expires_at ? dayjs(user.expires_at) : null
    });
    setEditModalVisible(true);
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '用户名',
      dataIndex: 'username',
      key: 'username',
      render: (text, record) => (
        <Space>
          <UserOutlined />
          <span>{text}</span>
          {record.is_superuser && <CrownOutlined style={{ color: '#722ed1' }} />}
        </Space>
      )
    },
    {
      title: '邮箱',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: '姓名',
      dataIndex: 'full_name',
      key: 'full_name',
      render: (text) => text || '-'
    },
    {
      title: '状态',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (isActive) => (
        <Badge 
          status={isActive ? 'success' : 'error'} 
          text={isActive ? '活跃' : '停用'} 
        />
      )
    },
    {
      title: '到期时间',
      dataIndex: 'expires_at',
      key: 'expires_at',
      render: (value) => {
        if (!value) return <Tag>未设置</Tag>;
        const expired = dayjs().isAfter(dayjs(value));
        const soon = !expired && dayjs(value).diff(dayjs(), 'day') <= 7;
        return (
          <Tag color={expired ? 'red' : (soon ? 'orange' : 'blue')}>
            {dayjs(value).format('YYYY-MM-DD HH:mm')}
          </Tag>
        );
      }
    },
    {
      title: '角色',
      dataIndex: 'is_superuser',
      key: 'is_superuser',
      render: (isSuperuser) => (
        <Tag color={isSuperuser ? 'purple' : 'blue'}>
          {isSuperuser ? '管理员' : '普通用户'}
        </Tag>
      )
    },
    {
      title: '注册时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (text) => new Date(text).toLocaleString()
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Space size="middle">
          <Tooltip title="编辑用户">
            <Button 
              type="text" 
              icon={<EditOutlined />} 
              onClick={() => openEditModal(record)}
            />
          </Tooltip>
          <Tooltip title={record.is_active ? '停用用户' : '激活用户'}>
            <Button 
              type="text" 
              icon={record.is_active ? <CloseCircleOutlined /> : <CheckCircleOutlined />}
              onClick={() => handleToggleActivation(record.id)}
            />
          </Tooltip>
          <Popconfirm
            title="确定要删除这个用户吗？"
            onConfirm={() => handleDeleteUser(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Tooltip title="删除用户">
              <Button 
                type="text" 
                danger 
                icon={<DeleteOutlined />}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Card>
          <div style={{ marginBottom: 16 }}>
            <Row justify="space-between" align="middle">
              <Col>
                <Title level={4} style={{ margin: 0 }}>用户管理</Title>
              </Col>
              <Col>
                <Space>
                  <Search
                    placeholder="搜索用户名、邮箱或姓名"
                    allowClear
                    enterButton={<SearchOutlined />}
                    size="middle"
                    onSearch={setSearchText}
                    style={{ width: 300 }}
                  />
                  <Button 
                    icon={<ReloadOutlined />} 
                    onClick={fetchUsers}
                  >
                    刷新
                  </Button>
                  <Button 
                    type="primary" 
                    icon={<PlusOutlined />}
                    onClick={() => setCreateModalVisible(true)}
                  >
                    新建用户
                  </Button>
                </Space>
              </Col>
            </Row>
          </div>

          <Table
            columns={columns}
            dataSource={users}
            rowKey="id"
            loading={loading}
            pagination={{
              ...pagination,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => 
                `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
              onChange: (page, pageSize) => {
                setPagination(prev => ({
                  ...prev,
                  current: page,
                  pageSize
                }));
              }
            }}
          />
        </Card>

        {/* 创建用户模态框 */}
        <Modal
          title="创建新用户"
          open={createModalVisible}
          onCancel={() => {
            setCreateModalVisible(false);
            createForm.resetFields();
          }}
          footer={null}
        >
          <Form
            form={createForm}
            layout="vertical"
            onFinish={handleCreateUser}
          >
            <Form.Item
              name="username"
              label="用户名"
              rules={[{ required: true, message: '请输入用户名' }]}
            >
              <Input placeholder="请输入用户名" />
            </Form.Item>

            <Form.Item
              name="email"
              label="邮箱"
              rules={[
                { required: true, message: '请输入邮箱' },
                { type: 'email', message: '请输入有效的邮箱地址' }
              ]}
            >
              <Input placeholder="请输入邮箱" />
            </Form.Item>

            <Form.Item
              name="password"
              label="密码"
              rules={[
                { required: true, message: '请输入密码' },
                { min: 8, message: '密码至少8位' }
              ]}
            >
              <Input.Password placeholder="请输入密码" />
            </Form.Item>

            <Form.Item
              name="full_name"
              label="姓名"
            >
              <Input placeholder="请输入姓名（可选）" />
            </Form.Item>

            <Form.Item
              name="expires_at"
              label="到期时间（可选）"
            >
              <DatePicker showTime style={{ width: '100%' }} />
            </Form.Item>

            <Form.Item
              name="is_superuser"
              label="用户角色"
              initialValue={false}
            >
              <Select>
                <Option value={false}>普通用户</Option>
                <Option value={true}>管理员</Option>
              </Select>
            </Form.Item>

            <Form.Item>
              <Space>
                <Button type="primary" htmlType="submit">
                  创建用户
                </Button>
                <Button onClick={() => {
                  setCreateModalVisible(false);
                  createForm.resetFields();
                }}>
                  取消
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>

        {/* 编辑用户模态框 */}
        <Modal
          title="编辑用户"
          open={editModalVisible}
          onCancel={() => {
            setEditModalVisible(false);
            setEditingUser(null);
            editForm.resetFields();
          }}
          footer={null}
        >
          <Form
            form={editForm}
            layout="vertical"
            onFinish={handleEditUser}
          >
            <Form.Item
              name="email"
              label="邮箱"
              rules={[
                { required: true, message: '请输入邮箱' },
                { type: 'email', message: '请输入有效的邮箱地址' }
              ]}
            >
              <Input placeholder="请输入邮箱" />
            </Form.Item>

            <Form.Item
              name="full_name"
              label="姓名"
            >
              <Input placeholder="请输入姓名（可选）" />
            </Form.Item>

            <Form.Item
              name="password"
              label="新密码"
              rules={[
                { min: 8, message: '密码至少8位' }
              ]}
            >
              <Input.Password placeholder="留空则不修改密码" />
            </Form.Item>

            <Form.Item
              name="is_active"
              label="用户状态"
              valuePropName="checked"
            >
              <Switch checkedChildren="活跃" unCheckedChildren="停用" />
            </Form.Item>

            <Form.Item
              name="is_superuser"
              label="用户角色"
            >
              <Select>
                <Option value={false}>普通用户</Option>
                <Option value={true}>管理员</Option>
              </Select>
            </Form.Item>

            <Form.Item
              name="expires_at"
              label="到期时间（可选）"
            >
              <DatePicker showTime style={{ width: '100%' }} />
            </Form.Item>

            <Form.Item>
              <Space>
                <Button type="primary" htmlType="submit">
                  更新用户
                </Button>
                <Button onClick={() => {
                  setEditModalVisible(false);
                  setEditingUser(null);
                  editForm.resetFields();
                }}>
                  取消
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>
    </div>
  );
};

export default UserManagement;